import {LobbyImage} from '@colonist/types'
import {type AjaxDataTypes} from '../../../../shared/AjaxDataTypes.ts'
import {type BaseStoreItem} from '../../../../shared/store/BaseStoreItem.ts'
import {type UserLoginState} from '../../../../shared/UserLoginState.ts'
import {UIAjaxWrapper} from '../network/UIAjaxWrapper.ts'
import {type SidebarTabInterface, UISidebarView} from '../ui_components/sidebar/UISidebarView.ts'
import {getProfilePopupElementId} from './UIViewProfilePopup.ts'
import {ClientUserState} from '../ClientUserState.ts'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables.ts'

export class ProfileSectionLink extends HTMLAnchorElement {
    hasMadeAPICall: boolean
}

export class UIProfileLinkController {
    private readonly overviewSection: HTMLDivElement
    private readonly rankedSection: HTMLDivElement
    private readonly historySection: HTMLDivElement
    private readonly itemsSection: HTMLDivElement
    private readonly friendsSection?: HTMLDivElement
    private readonly settingsSection?: HTMLDivElement

    readonly overviewSectionLink: ProfileSectionLink
    readonly rankedSectionLink: ProfileSectionLink
    readonly historySectionLink: ProfileSectionLink
    readonly itemsSectionLink: ProfileSectionLink
    readonly friendsSectionLink?: ProfileSectionLink
    readonly settingsSectionLink?: ProfileSectionLink

    private readonly sections: HTMLDivElement[]
    readonly sectionLinks: ProfileSectionLink[]
    activeLink: ProfileSectionLink

    private readonly isPopup: boolean
    private username: string

    constructor(isPopup: boolean = false, username: string = '') {
        this.isPopup = isPopup
        this.username = username
        this.createSidebar()

        this.overviewSection = document.getElementById(getProfilePopupElementId('overview-container', isPopup)) as HTMLDivElement
        this.rankedSection = document.getElementById(getProfilePopupElementId('ranked-container', isPopup)) as HTMLDivElement
        this.historySection = document.getElementById(getProfilePopupElementId('game-history-container', isPopup)) as HTMLDivElement
        this.itemsSection = document.getElementById(getProfilePopupElementId('items-container', isPopup)) as HTMLDivElement

        this.overviewSectionLink = document.getElementById(getProfilePopupElementId('tab-input-overview', isPopup)) as ProfileSectionLink
        this.rankedSectionLink = document.getElementById(getProfilePopupElementId('tab-input-ranked', isPopup)) as ProfileSectionLink
        this.historySectionLink = document.getElementById(getProfilePopupElementId('tab-input-game-history', isPopup)) as ProfileSectionLink
        this.itemsSectionLink = document.getElementById(getProfilePopupElementId('tab-input-items', isPopup)) as ProfileSectionLink

        this.sections = [this.overviewSection, this.rankedSection, this.historySection, this.itemsSection]
        this.sectionLinks = [this.overviewSectionLink, this.rankedSectionLink, this.historySectionLink, this.itemsSectionLink]

        if(!this.isPopup) {
            this.friendsSection = document.getElementById(getProfilePopupElementId('friends-container', isPopup)) as HTMLDivElement
            this.settingsSection = document.getElementById(getProfilePopupElementId('settings-container', isPopup)) as HTMLDivElement
            this.friendsSectionLink = document.getElementById(getProfilePopupElementId('tab-input-friends', isPopup)) as ProfileSectionLink
            this.settingsSectionLink = document.getElementById(getProfilePopupElementId('tab-input-settings', isPopup)) as ProfileSectionLink

            this.sections.push(this.friendsSection as HTMLDivElement, this.settingsSection as HTMLDivElement)
            this.sectionLinks.push(this.friendsSectionLink as ProfileSectionLink, this.settingsSectionLink as ProfileSectionLink)
        }

        this.activeLink = this.overviewSectionLink
    }

    private createSidebar() {
        const profileTabsInfo: SidebarTabInterface[] = [
            {
                text: {key: 'strings:profilePage.overview.title'},
                class: 'icon_profile_overview',
                href: '#overview',
                imageSrc: LobbyImage.ProfileOverview,
                id: getProfilePopupElementId('tab-input-overview', this.isPopup),
            },
            {
                text: {key: 'strings:profilePage.navigationTabs.ranked'},
                class: 'icon_profile_ranked',
                href: '#ranked',
                imageSrc: LobbyImage.ProfileRanked,
                id: getProfilePopupElementId('tab-input-ranked', this.isPopup),
            },
            {
                text: {key: 'strings:profilePage.navigationTabs.history'},
                class: 'icon_profile_history',
                href: '#history',
                imageSrc: LobbyImage.ProfileHistory,
                id: getProfilePopupElementId('tab-input-game-history', this.isPopup),
            },
            {
                text: {key: 'strings:profilePage.navigationTabs.items'},
                class: 'icon_profile_items',
                href: '#items',
                imageSrc: LobbyImage.ProfileItems,
                id: getProfilePopupElementId('tab-input-items', this.isPopup),
            },
        ]

        if(!this.isPopup) {
            profileTabsInfo.push({
                text: {key: 'strings:profilePage.navigationTabs.friends'},
                class: 'icon_profile_friends',
                href: '#friends',
                imageSrc: LobbyImage.Friends,
                id: getProfilePopupElementId('tab-input-friends', this.isPopup),
            })
            profileTabsInfo.push({
                text: {key: 'strings:profilePage.navigationTabs.account'},
                class: 'icon_profile_settings',
                href: '#settings',
                imageSrc: LobbyImage.ProfileSettings,
                id: getProfilePopupElementId('tab-input-settings', this.isPopup),
            })
        }

        const sidebarElement = document.getElementById(getProfilePopupElementId('profile_sidebar', this.isPopup)) as HTMLDivElement

        new UISidebarView(sidebarElement, profileTabsInfo)
    }

    showSection(newSectionIdx: number): void {
        if(!this.isCurrentUserProfile() && ['#friends', '#settings'].includes(this.sectionLinks[newSectionIdx].hash)) newSectionIdx = 0
        for(let index = 0; index < this.sections.length; index++) {
            const contentSection = this.sections[index]
            const tab = this.sectionLinks[index]

            if(index == newSectionIdx) {
                contentSection.classList.remove('hidden')
                tab.classList.add('active')
            } else {
                contentSection.classList.add('hidden')
                tab.classList.remove('active')
            }
        }
        this.activeLink = this.sectionLinks[newSectionIdx]
        const hash = this.sectionLinks[newSectionIdx].hash
        if(!hash) return
        if(UIUtilsEjsVariables.isUserOnDiscord) return
        const isProfilePage = window.location.pathname.includes('/profile')
        if(!isProfilePage) return

        history.replaceState(null, '', window.location.pathname + window.location.search + hash)
    }

    showSectionFromHash(): boolean {
        const hash = window.location.hash.replace('#', '')
        if(hash == '') return false

        for(let index = 0; index < this.sections.length; index++) {
            const section = this.sections[index]
            if(section.id.includes(hash) == true) {
                this.showSection(index)
                return true
            }
        }
        return false
    }

    setUsername(username: string): void {
        this.username = username
    }

    private isCurrentUserProfile(): boolean {
        return ClientUserState.userState?.username.toLowerCase() == decodeURIComponent(this.username.toLowerCase())
    }

    static async genericApiCallWrapper<T extends BaseStoreItem[] | AjaxDataTypes.ProfileHistoryData | AjaxDataTypes.PublicProfileDataOverview | AjaxDataTypes.ProfileDataRankedSeasonDropdown[] | UserLoginState[]>(link: ProfileSectionLink, endpoint: string, callback: (input: T) => void) {
        if(link.hasMadeAPICall) return
        const response = await UIAjaxWrapper.getRequestHandler(endpoint, undefined)
        if(response == undefined) return
        if(!response.isSuccess) return
        const items = response.json as T
        callback(items)
        link.hasMadeAPICall = true
    }

    resetApiCalls() {
        this.sectionLinks.forEach((link) => {
            link.hasMadeAPICall = false
        })
    }
}
